import { computed, effect, inject, Injectable, signal } from '@angular/core';
import maplibregl, { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { HasPermissionsService, OperatorEnum } from '@connect-services/client/services/has-permissions.service';
import {
    CreatePinDto,
    PermissionAliasEnum,
    Pin,
    PinControllerGetRequestParams,
    PinsService,
    UpdatePinDto,
} from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class PinService {
    public readonly canManage = computed<boolean>(() =>
        this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_PIN]),
    );
    public readonly canRead = computed<boolean>(() =>
        this.hasPermissionsService.checkPermissions(
            [PermissionAliasEnum.READ_PIN, PermissionAliasEnum.MANAGE_PIN],
            OperatorEnum.OR,
        ),
    );
    public readonly isActive = signal<boolean>(false);
    public readonly isCreateModalOpen = signal<boolean>(false);
    public readonly isSelectingPoint = signal<boolean>(false);
    public readonly pins = signal<Pin[]>([]);

    private readonly fetchAllService = inject(FetchAllService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private map?: MapLibreMap;
    private readonly markers = new Map<string, maplibregl.Marker>();
    private readonly pinsService = inject(PinsService);
    private readonly projectBloc = inject(ProjectBloc);

    public constructor() {
        effect(() => {
            const projectId = this.projectBloc.value()?.id;

            if (!projectId || !this.canRead()) {
                this.reset();

                return;
            }

            void this.load(projectId);
        });

        effect(() => {
            this.syncMarkers(this.pins(), this.isActive());
        });
    }

    public beginCreate(): void {
        if (!this.canManage() || this.isCreateModalOpen()) {
            return;
        }

        this.isSelectingPoint.set(true);
        this.updateCursor();
    }

    public bindMap(map: MapLibreMap): void {
        this.map = map;
        this.syncMarkers(this.pins(), this.isActive());
        this.updateCursor();
    }

    public cancelCreate(): void {
        this.isSelectingPoint.set(false);
        this.updateCursor();
    }

    public close(): void {
        this.isActive.set(false);
        this.cancelCreate();
    }

    public async create(dto: Omit<CreatePinDto, 'coordinates'>, coordinates: [number, number]): Promise<void> {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        await firstValueFrom(
            this.pinsService.pinControllerPost({
                createPinDto: {
                    ...dto,
                    coordinates: {
                        latitude: coordinates[1],
                        longitude: coordinates[0],
                    },
                },
                id: projectId,
            }),
        );

        this.cancelCreate();
        await this.load(projectId);
    }

    public async delete(pinId: string): Promise<void> {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        await firstValueFrom(this.pinsService.pinControllerDelete({ id: projectId, pinId }));
        await this.load(projectId);
    }

    public handleMapClick(event: MapMouseEvent): [number, number] | null {
        if (!this.isSelectingPoint() || this.isCreateModalOpen()) {
            return null;
        }

        this.isSelectingPoint.set(false);
        this.updateCursor();

        return [event.lngLat.lng, event.lngLat.lat];
    }

    public toggle(): void {
        if (!this.canRead()) {
            return;
        }

        this.isActive.update((isActive: boolean) => !isActive);

        if (!this.isActive()) {
            this.cancelCreate();
        }
    }

    public async update(pinId: string, dto: UpdatePinDto): Promise<void> {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        await firstValueFrom(this.pinsService.pinControllerPatch({ id: projectId, pinId, updatePinDto: dto }));
        await this.load(projectId);
    }

    public zoomTo(pin: Pin): void {
        this.map?.flyTo({
            center: [pin.geom.coordinates[0], pin.geom.coordinates[1]],
            essential: true,
            speed: 1.5,
            zoom: 18,
        });
    }

    private createMarker(pin: Pin): maplibregl.Marker {
        const element = document.createElement('button');
        element.className = 'pin-map-marker';
        element.title = pin.description || pin.name;
        element.type = 'button';
        element.innerHTML = `
            <svg aria-hidden="true" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 0C5.925 0 1 4.925 1 11c0 8.25 11 21 11 21s11-12.75 11-21C23 4.925 18.075 0 12 0Z" fill="${pin.color}"/>
                <circle cx="12" cy="11" fill="#fff" r="4"/>
            </svg>
            <span>${this.escapeHtml(pin.name)}</span>
        `;
        element.onclick = (): void => this.zoomTo(pin);

        return new maplibregl.Marker({ anchor: 'bottom', element }).setLngLat([
            pin.geom.coordinates[0],
            pin.geom.coordinates[1],
        ]);
    }

    private escapeHtml(value: string): string {
        const element = document.createElement('span');
        element.textContent = value;

        return element.innerHTML;
    }

    private async load(projectId: string): Promise<void> {
        const pins = await this.fetchAllService.getAll<PinControllerGetRequestParams, Pin>(
            this.pinsService.pinControllerGet.bind(this.pinsService),
            { id: projectId },
        );

        this.pins.set(pins);
    }

    private reset(): void {
        this.close();
        this.pins.set([]);
    }

    private syncMarkers(pins: Pin[], isActive: boolean): void {
        this.markers.forEach((marker: maplibregl.Marker) => marker.remove());
        this.markers.clear();

        if (!this.map || !isActive) {
            return;
        }

        pins.forEach((pin: Pin): void => {
            const marker = this.createMarker(pin).addTo(this.map!);
            this.markers.set(pin.id, marker);
        });
    }

    private updateCursor(): void {
        if (this.map) {
            this.map.getCanvas().style.cursor = this.isSelectingPoint() ? 'crosshair' : '';
        }
    }
}
